import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Box,
  Chip,
  TextField,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Modal from '../../../../components/Modal';
import Form from '../../../../components/useForm';
import { Controls } from '../../../../components/controls/Controls';

export default function AccuroProvidersConfigurationModal(props) {
  const {
    isOpen,
    handleClose,
    setting,
    handleSave,
    providers,
  } = props;

  const [selectedProviders, setSelectedProviders] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const providerIdSet = new Set(setting.value.split(',').map(Number));
      const providersFromExistingSetting = providers.filter((p) => providerIdSet.has(p.providerId));
      setSelectedProviders(providersFromExistingSetting);
    }
  }, [isOpen]);

  function generateProviderLabel(provider) {
    return `${provider.title} ${provider.firstName} ${provider.lastName}`;
  }

  const saveProviders = async () => {
    const selectedIds = selectedProviders.map(
      (provider) => provider.providerId.toString(),
    );
    await handleSave(setting, selectedIds.join(','));
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      title="Providers Configuration"
    >
      <Form>
        <Grid
          container
          direction="column"
          spacing={2}
        >
          <Grid item>
            <Autocomplete
              multiple
              id="provider-select"
              options={providers}
              disableCloseOnSelect
              getOptionLabel={(option) => generateProviderLabel(option)}
              value={selectedProviders}
              onChange={(event, newValue) => {
                setSelectedProviders(newValue);
              }}
              isOptionEqualToValue={(option, value) => option.providerId === value.providerId}
              renderOption={(optionProps, option, { selected }) => (
                <Box
                  component="li"
                  key={option.providerId}
                  {...optionProps} // eslint-disable-line react/jsx-props-no-spreading
                >
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {generateProviderLabel(option)}
                </Box>
              )}
              style={{ minWidth: 300, maxWidth: 400 }}
              renderInput={(params) => (
                <TextField
                  {...params} // eslint-disable-line react/jsx-props-no-spreading
                  label="Provider"
                  placeholder="Search providers..."
                  fullWidth
                />
              )}
              renderTags={() => null}
            />
          </Grid>
          {selectedProviders.length > 0 && (
            <Grid item>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedProviders.map((provider) => (
                  <Chip
                    key={provider.providerId}
                    label={generateProviderLabel(provider)}
                    onDelete={() => {
                      setSelectedProviders(
                        selectedProviders.filter((p) => p.providerId !== provider.providerId),
                      );
                    }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          )}
          <Grid item>
            <Controls.Button
              text="save"
              onClick={saveProviders}
            />
          </Grid>
        </Grid>
      </Form>
    </Modal>
  );
}

const settingShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

AccuroProvidersConfigurationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  setting: settingShape.isRequired,
  handleSave: PropTypes.func.isRequired,
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      providerId: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      middleName: PropTypes.string.isRequired,
    }),
  ).isRequired,
};
